import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";

const STATUSES = ["all", "new", "contacted", "closed"];
const STATUS_COLORS = {
  new: "bg-primary/10 text-primary border-primary/40",
  contacted: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  closed: "bg-foreground/5 text-foreground/50 border-foreground/25",
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    api
      .get("/admin/leads")
      .then((r) => setLeads(r.data))
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const shown = filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
          Leads inbox <span className="text-foreground/40 text-2xl">({shown.length})</span>
        </h2>
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`leads-filter-${s}`}
              className={`btn-sharp border px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-medium ${
                filter === s
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-foreground/30 text-foreground/65"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-foreground/50">Loading leads…</div>
      ) : shown.length === 0 ? (
        <div className="py-20 text-center text-sm text-foreground/50" data-testid="leads-empty">
          No leads here yet.
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((l) => (
            <article
              key={l.id}
              data-testid={`lead-row-${l.id}`}
              className="border border-foreground/15 p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4"
            >
              <div>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="font-serif text-xl md:text-2xl">{l.name}</span>
                  <a href={`mailto:${l.email}`} className="text-sm text-primary hover:underline">{l.email}</a>
                  {l.phone && <span className="text-sm text-foreground/60">{l.phone}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-foreground/60">
                  {l.interest && <span><strong className="text-foreground/80">Interest:</strong> {l.interest}</span>}
                  {l.travel_date && <span><strong className="text-foreground/80">Dates:</strong> {l.travel_date}</span>}
                  {l.nights && <span><strong className="text-foreground/80">Nights:</strong> {l.nights}</span>}
                  {l.travelers && <span><strong className="text-foreground/80">Travellers:</strong> {l.travelers}</span>}
                  <span><strong className="text-foreground/80">Via:</strong> {l.source}</span>
                  <span>{new Date(l.created_at).toLocaleString()}</span>
                </div>
                {l.message && (
                  <p className="mt-3 text-sm font-light text-foreground/75 leading-relaxed border-l-2 border-primary/40 pl-4">
                    {l.message}
                  </p>
                )}
              </div>
              <div className="flex md:flex-col gap-2 items-start">
                <span
                  className={`text-[9px] uppercase tracking-[0.22em] border px-3 py-1.5 ${STATUS_COLORS[l.status || "new"]}`}
                  data-testid={`lead-status-${l.id}`}
                >
                  {l.status || "new"}
                </span>
                <div className="flex gap-1.5">
                  {["new", "contacted", "closed"]
                    .filter((s) => s !== (l.status || "new"))
                    .map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(l, s)}
                        data-testid={`lead-set-${s}-${l.id}`}
                        className="text-[9px] uppercase tracking-[0.18em] border border-foreground/25 px-2.5 py-1.5 text-foreground/60 hover:border-primary hover:text-primary transition-colors"
                      >
                        → {s}
                      </button>
                    ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
